// MarkDeliveredModal — confirms delivery for a job at status='new'.
//
// Wired to POST /api/v1/skip/jobs/:enc_id/actions/mark_delivered. The
// server stamps delivered_at, optionally writes driver_id, optionally
// appends a note row, and flips the bound inventory unit's status to
// 'out' in the same handler.
//
// The "Add Details" section is collapsed by default — operators
// usually just click "Mark Delivered" with no extras. Date and time
// inputs pre-fill to today / now so the common case (capturing
// after-the-fact) takes one click.
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '../../../../components/ui/Button/Button';
import { Modal } from '../../../../components/ui/Modal/Modal';
import { apiClient } from '../../../../api/client';
import { queryKeys } from '../../../../api/queryKeys';
import { extractErrorMessage } from '../../../../utils/errorMessage';
import { useDriversList } from '../../../admin/drivers/hooks/useDriversAdmin';

import styles from './MarkDeliveredModal.module.css';

export interface MarkDeliveredModalProps {
  isOpen: boolean;
  jobEncId: string;
  jobNumber: string;
  customerName: string;
  onClose: () => void;
  onSuccess?: () => void;
}

interface MarkDeliveredBody {
  delivered_at?: string;
  driver_id_enc?: string;
  notes?: string;
}

function todayIsoDate(): string {
  const now = new Date();
  const yyyy = String(now.getFullYear()).padStart(4, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function nowHHMM(): string {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function combineToIsoDateTime(date: string, time: string): string {
  // Time may be empty — fall back to T00:00. Treat as local time and
  // emit as ISO with the local offset.
  const t = time && /^\d{2}:\d{2}$/.test(time) ? time : '00:00';
  const d = new Date(`${date}T${t}:00`);
  return d.toISOString();
}

export function MarkDeliveredModal({
  isOpen,
  jobEncId,
  jobNumber,
  customerName,
  onClose,
  onSuccess,
}: MarkDeliveredModalProps): JSX.Element | null {
  const qc = useQueryClient();
  const driversQuery = useDriversList({ page: 1, limit: 100, status: 'active' });

  const [showDetails, setShowDetails] = useState(false);
  const [date, setDate] = useState<string>(todayIsoDate());
  const [time, setTime] = useState<string>(nowHHMM());
  const [driverEncId, setDriverEncId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form whenever the modal is reopened so a previous error /
  // typed values don't bleed into the next invocation.
  useEffect(() => {
    if (isOpen) {
      setShowDetails(false);
      setDate(todayIsoDate());
      setTime(nowHHMM());
      setDriverEncId('');
      setNotes('');
      setError(null);
    }
  }, [isOpen]);

  const drivers = useMemo(() => driversQuery.data?.drivers ?? [], [driversQuery.data]);

  if (!isOpen) return null;

  function close(): void {
    if (submitting) return;
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    setError(null);
    setSubmitting(true);
    const body: MarkDeliveredBody = {};
    if (showDetails) {
      // Include details only when the section was actually opened.
      // Date is required-ish (always pre-filled to today); time
      // defaults if blank.
      if (date) {
        body.delivered_at = combineToIsoDateTime(date, time);
      }
      if (driverEncId) body.driver_id_enc = driverEncId;
      if (notes.trim().length > 0) body.notes = notes.trim();
    }
    try {
      await apiClient.post(
        `/skip/jobs/${encodeURIComponent(jobEncId)}/actions/mark_delivered`,
        body,
      );
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.detail(jobEncId) });
      void qc.invalidateQueries({
        queryKey: queryKeys.skipJobs.availableActions(jobEncId),
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onClose={close}
      title="Mark Delivered"
      size="md"
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" onClick={close} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Marking…' : 'Mark Delivered'}
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        <p className={styles.confirm}>
          Confirm the skip has been delivered to{' '}
          <strong>{customerName}</strong> for job <strong>{jobNumber}</strong>.
        </p>

        <button
          type="button"
          className={styles.toggle}
          onClick={() => setShowDetails((v) => !v)}
          aria-expanded={showDetails}
        >
          {showDetails ? 'Hide Details ▴' : 'Add Details ▾'}
        </button>

        {showDetails ? (
          <div className={styles.details}>
            <div className={styles.row}>
              <label className={styles.field}>
                <span className={styles.label}>Delivered date</span>
                <input
                  type="date"
                  className={styles.input}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Delivered time</span>
                <input
                  type="time"
                  className={styles.input}
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </label>
            </div>
            <label className={[styles.field, styles.fieldFull].join(' ')}>
              <span className={styles.label}>Driver</span>
              <select
                className={styles.select}
                value={driverEncId}
                onChange={(e) => setDriverEncId(e.target.value)}
              >
                <option value="">— Select driver (optional) —</option>
                {drivers.map((d) => (
                  <option key={d.uuid} value={d.uuid}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label className={[styles.field, styles.fieldFull].join(' ')}>
              <span className={styles.label}>Additional notes</span>
              <textarea
                className={styles.textarea}
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Delivery notes…"
                maxLength={2000}
              />
            </label>
          </div>
        ) : null}

        {error ? <p className={styles.error}>{error}</p> : null}
      </div>
    </Modal>
  );
}

